import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Loader2, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import signinTemple from "@/assets/signin-temple.jpg";
import headerCrest from "@/assets/header-crest.png";
import decorumLogo from "@/assets/decorum-logo-transparent.png";
import { trackEvent } from "@/lib/analytics";
import { lovable } from "@/integrations/lovable/index";

interface AssessmentData {
  stage: number;
  encumbranceLevel: string;
  encumbranceDescription: string;
  answers: Record<string, string | number>;
  score: number;
}

const Auth = ({ previewMode = false }: { previewMode?: boolean }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const assessmentData = location.state as AssessmentData | null;
  const fromAssessment = !!assessmentData?.answers;
  // Post-checkout "Create your account" links here with ?mode=signup.
  const wantSignup = false;

  useEffect(() => {
    if (previewMode) return;
    if (!user) return;
    if (fromAssessment && assessmentData) {
      saveAssessmentData(user.id, assessmentData);
    }
    // A pending founding-member code (from an admin bypass link opened while
    // signed out) must be redeemed before we drop the user into the paywalled app.
    let pendingCode: string | null = null;
    try {
      pendingCode = window.localStorage.getItem("pending_founding_code");
    } catch {
      pendingCode = null;
    }
    if (pendingCode?.trim()) {
      navigate(`/redeem?code=${encodeURIComponent(pendingCode.trim())}`, { replace: true });
      return;
    }
    let back: string | null = null;
    try {
      back = window.localStorage.getItem("post_auth_redirect");
      if (back) window.localStorage.removeItem("post_auth_redirect");
    } catch {
      back = null;
    }
    navigate(back && back.startsWith("/book/") ? back : "/dashboard", { replace: true });
  }, [user, navigate, previewMode, fromAssessment, assessmentData]);

  const saveAssessmentData = async (userId: string, data: AssessmentData) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          encumbrance_level: data.encumbranceLevel,
          encumbrance_score: data.score,
          assessment_answers: data.answers,
          assessment_completed_at: new Date().toISOString(),
        })
        .eq("user_id", userId);
      if (error) console.error("Error saving assessment data:", error);
    } catch (err) {
      console.error("Failed to save assessment data:", err);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Missing fields", description: "Please enter both email and password.", variant: "destructive" });
      return;
    }
    trackEvent("auth_signin_attempt");
    setIsLoading(true);
    const { error } = await signIn(email, password);
    setIsLoading(false);
    if (error) {
      trackEvent("auth_signin_failed", { reason: error.message });
      toast({ title: "Sign in failed", description: error.message, variant: "destructive" });
    } else {
      trackEvent("auth_signin_success");
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    toast({ title: "Invitation required", description: "Use the private link Lance sends after your interview." });
    return;

  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ title: "Missing email", description: "Please enter your email address.", variant: "destructive" });
      return;
    }
    trackEvent("auth_password_reset_attempt");
    setIsResettingPassword(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`,
    });
    setIsResettingPassword(false);
    if (error) {
      trackEvent("auth_password_reset_failed", { reason: error.message });
      toast({ title: "Reset failed", description: error.message, variant: "destructive" });
    } else {
      trackEvent("auth_password_reset_sent");
      toast({ title: "Check your email", description: "We've sent you a password reset link." });
      setShowResetForm(false);
    }
  };

  const inputClasses = "h-12 border-[hsl(36_35%_70%)] bg-[hsl(40_45%_96%)] text-[hsl(30_25%_15%)] placeholder:text-[hsl(30_10%_55%)] focus:border-[hsl(36_60%_45%)] focus:ring-[hsl(36_60%_45%/0.25)] font-serif text-base";
  const labelClasses = "text-[11px] uppercase tracking-[0.18em] font-serif font-semibold text-[hsl(30_25%_20%)]";
  const goldBtn = { background: "linear-gradient(180deg, hsl(38 55% 50%), hsl(34 55% 34%))", color: "hsl(40 50% 96%)" };
  const triggerCls = "rounded-none bg-transparent font-serif text-lg text-[hsl(30_10%_45%)] border-b-2 border-transparent data-[state=active]:!bg-transparent data-[state=active]:!text-[hsl(34_55%_35%)] data-[state=active]:shadow-none data-[state=active]:text-[hsl(34_55%_35%)] data-[state=active]:border-[hsl(34_55%_40%)] pb-2";

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[hsl(40_40%_92%)]">
      {/* Painted temple */}
      <div className="relative hidden lg:block lg:sticky lg:top-0 lg:h-screen">
        <img src={signinTemple} alt="Centurion kneeling with a crown before the temple" className="absolute inset-0 w-full h-full object-cover object-center" />
      </div>
      <div className="relative lg:hidden h-56 sm:h-72">
        <img src={signinTemple} alt="" className="absolute inset-0 w-full h-full object-cover object-[30%_60%]" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[hsl(40_40%_92%)]" />
      </div>

      {/* Marble card */}
      <div className="flex items-center justify-center p-4 sm:p-8"
        style={{ backgroundImage: "radial-gradient(ellipse at top, hsl(40 45% 96%), hsl(38 35% 88%))" }}>
        <div className="w-full max-w-lg border border-[hsl(36_50%_55%)] p-1.5 animate-fade-up">
          <div className="border border-[hsl(36_50%_60%/0.6)] bg-[hsl(40_45%_95%/0.7)] px-6 sm:px-10 py-8">
            <div className="flex justify-center mb-3">
              <img src={headerCrest} alt="" className="h-24 w-auto" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-center text-[hsl(30_25%_15%)]">Enter the Kingdom Hub</h1>
            <p className="font-serif text-center text-[hsl(30_15%_30%)] mt-2 mb-6">Sign in to continue your training.</p>

            <Button type="button" variant="outline" disabled={isLoading}
              className="w-full h-12 mb-5 font-serif text-base rounded-md border-[hsl(36_50%_55%)] bg-[hsl(40_45%_97%)] text-[hsl(30_25%_15%)] hover:bg-[hsl(40_45%_92%)]"
              onClick={async () => {
                trackEvent("auth_google_attempt");
                const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: `${window.location.origin}/auth` });
                if (result.error) toast({ title: "Google sign in failed", description: String(result.error.message ?? result.error), variant: "destructive" });
              }}>
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 mb-5 text-[11px] uppercase tracking-[0.18em] font-serif text-[hsl(30_10%_45%)]">
              <span className="h-px flex-1 bg-[hsl(36_35%_70%)]" />or<span className="h-px flex-1 bg-[hsl(36_35%_70%)]" />
            </div>
            <Tabs defaultValue={wantSignup ? "signup" : "signin"} className="w-full">
              <TabsList className="grid w-full grid-cols-1 h-auto bg-transparent p-0 mb-6 max-w-xs mx-auto">
                <TabsTrigger value="signin" data-gold-active={undefined} className={triggerCls}>Sign In</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="mt-0">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="signin-email" className={labelClasses}>Email</Label>
                    <Input id="signin-email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoading} className={inputClasses} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signin-password" className={labelClasses}>Password</Label>
                    <Input id="signin-password" type="password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={isLoading} className={inputClasses} />
                  </div>
                  <Button type="submit" className="w-full h-12 font-serif text-lg mt-2 rounded-md" disabled={isLoading} style={goldBtn}>
                    {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Entering...</> : <>Enter the Kingdom Hub <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                  <button type="button" onClick={() => setShowResetForm(true)} className="w-full font-serif text-sm underline text-[hsl(34_55%_35%)] hover:text-[hsl(34_55%_25%)]">
                    Forgot your password?
                  </button>
                </form>
              </TabsContent>

            </Tabs>

            <div className="border-t border-[hsl(36_35%_75%)] mt-7 pt-5 text-center font-serif text-[hsl(30_20%_20%)]">
              Membership is by invitation after an interview. <a href="/#contact" className="underline">Apply for coaching.</a>
            </div>
            <a href="/" className="inline-flex items-center gap-2 mt-6 font-serif text-[hsl(30_20%_20%)] hover:text-[hsl(34_55%_35%)]">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </a>
          </div>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showResetForm} onOpenChange={setShowResetForm}>
        <DialogContent className="sm:max-w-md bg-[hsl(40_45%_95%)] border-[hsl(36_50%_55%)] text-[hsl(30_25%_15%)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-[hsl(34_55%_35%)]">
              <Mail className="h-5 w-5" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-[hsl(30_15%_35%)]">
              Enter your email and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reset-email" className={labelClasses}>Email</Label>
              <Input id="reset-email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isResettingPassword} className={inputClasses} />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1 h-10 bg-transparent border-[hsl(36_50%_55%)] text-[hsl(30_25%_20%)]" onClick={() => setShowResetForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 h-10 font-semibold" disabled={isResettingPassword} style={goldBtn}>
                {isResettingPassword ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : "Send Reset Link"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
